import { useTenant } from "@/providers/session";
import type { QueryOperator } from "@/types/utils";
import type { OnChangeFn, SortingState } from "@tanstack/react-table";
import { createParser, useQueryState } from "nuqs";
import { useCallback, useEffect, useMemo, useState } from "react";

type FilterValue = {
  value: null | string | string[] | number | number[] | boolean;
  op: QueryOperator;
};

type LikeFilter = { value: string; op: "iLike" | "notILike" };

type RawFilters<T extends string = string> = Partial<
  Record<
    T,
    | null
    | string
    | string[]
    | number
    | boolean
    | { from?: Date; to?: Date }
    | { min?: number; max?: number }
    | LikeFilter
  >
>;

type ServerFilters<T extends string = string> = Record<T, FilterValue>;

export const getQueryKey = (queryKeyPrefix: string, queryKey: string) => {
  return `${queryKeyPrefix ? `${queryKeyPrefix}_` : ""}${queryKey}`;
};

const isLikeFilter = (value: unknown): value is LikeFilter => {
  return (
    typeof value === "object" &&
    value !== null &&
    "value" in value &&
    "op" in value &&
    ((value as LikeFilter).op === "iLike" ||
      (value as LikeFilter).op === "notILike")
  );
};

const flattenNestedFields = (
  obj: Record<string, any>,
  prefix = "",
): Record<string, any> => {
  const result: Record<string, any> = {};

  Object.entries(obj).forEach(([key, value]) => {
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      !("from" in value || "to" in value) &&
      !("min" in value || "max" in value) &&
      !isLikeFilter(value)
    ) {
      Object.assign(result, flattenNestedFields(value, newKey));
    } else {
      result[newKey] = value;
    }
  });

  return result;
};

const unflattenNestedFields = (
  obj: Record<string, any>,
): Record<string, any> => {
  const result: Record<string, any> = {};

  Object.entries(obj).forEach(([key, value]) => {
    const keys = key.split(".");
    let current = result;

    for (let i = 0; i < keys.length - 1; i++) {
      const currentKey = keys[i];
      if (!(currentKey in current)) {
        current[currentKey] = {};
      }
      current = current[currentKey];
    }

    current[keys[keys.length - 1]] = value;
  });

  return result;
};

export const convertToServerFilters = <T extends string>(
  rawFilters: RawFilters<T>,
  defaultOp: QueryOperator = "eq",
): ServerFilters<string> => {
  const flattenedFilters = flattenNestedFields(rawFilters);
  const result: ServerFilters<string> = {} as ServerFilters<string>;

  Object.entries(flattenedFilters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      // Skip "all" values for side filter (means no filter applied)
      if (key === "side" && value === "all") {
        return;
      }

      if (
        typeof value === "object" &&
        !Array.isArray(value) &&
        ("from" in value || "to" in value)
      ) {
        const dateRange = value as { from?: string | Date; to?: string | Date };
        const dates: string[] = [];

        if (dateRange.from) {
          const fromDate = new Date(dateRange.from);
          if (!Number.isNaN(fromDate.getTime())) {
            dates.push(fromDate.toISOString());
          }
        }
        if (dateRange.to) {
          const toDate = new Date(dateRange.to);
          if (!Number.isNaN(toDate.getTime())) {
            dates.push(toDate.toISOString());
          }
        }

        if (dates.length > 0) {
          result[key] = {
            value: dates.length === 1 ? dates[0] : dates,
            op:
              dates.length === 1 ? (dateRange.from ? "gte" : "lte") : "between",
          };
        }
      } else if (
        typeof value === "object" &&
        !Array.isArray(value) &&
        ("min" in value || "max" in value)
      ) {
        const range = value as { min?: number; max?: number };
        const values: number[] = [];

        if (range.min !== undefined) values.push(range.min);
        if (range.max !== undefined) values.push(range.max);

        if (values.length > 0) {
          result[key] = {
            value: values.length === 1 ? values[0] : values,
            op:
              values.length === 1
                ? range.min !== undefined
                  ? "min"
                  : "max"
                : "between",
          };
        }
      } else if (isLikeFilter(value)) {
        result[key] = {
          value: value.value,
          op: value.op,
        };
      } else if (Array.isArray(value)) {
        const filteredArray = value.filter(
          (item) =>
            item !== undefined &&
            item !== null &&
            item !== "" &&
            String(item).trim() !== "",
        );

        if (filteredArray.length > 0) {
          result[key] = {
            value: filteredArray as string[] | number[],
            op: "in",
          };
        }
      } else {
        result[key] = {
          value: value as string | number | boolean,
          op: defaultOp,
        };
      }
    }
  });

  return result;
};

const filtersParser = createParser({
  parse: (value: string): RawFilters => {
    if (!value) return {};

    try {
      const params = new URLSearchParams(value);
      const flatResult: RawFilters = {};

      params.forEach((paramValue, key) => {
        if (key.includes("_")) {
          const lastUnderscoreIndex = key.lastIndexOf("_");
          const field = key.slice(0, lastUnderscoreIndex);
          const operator = key.slice(lastUnderscoreIndex + 1) as QueryOperator;

          let parsedValue: any = paramValue;

          if (paramValue.includes(",")) {
            parsedValue = paramValue.split(",").map((v) => v.trim());
          }

          if (!Number.isNaN(Number(paramValue))) {
            parsedValue = Number(paramValue);
          }

          if (paramValue === "true") parsedValue = true;
          if (paramValue === "false") parsedValue = false;

          if (
            operator === "between" &&
            Array.isArray(parsedValue) &&
            parsedValue.length === 2
          ) {
            const isDateRange = parsedValue.every((v) => !isNaN(Date.parse(v)));

            if (isDateRange) {
              flatResult[field] = {
                from: new Date(parsedValue[0]),
                to: new Date(parsedValue[1]),
              };
            } else {
              flatResult[field] = {
                min: Number(parsedValue[0]),
                max: Number(parsedValue[1]),
              };
            }
          } else if (operator === "min") {
            flatResult[field] = { min: Number(parsedValue) };
          } else if (operator === "max") {
            flatResult[field] = { max: Number(parsedValue) };
          } else if (operator === "in") {
            if (!Array.isArray(parsedValue)) {
              parsedValue = [parsedValue];
            }
            flatResult[field] = parsedValue;
          } else if (operator === "iLike" || operator === "notILike") {
            flatResult[field] = { value: parsedValue, op: operator };
          } else {
            flatResult[field] = parsedValue;
          }
        }
      });

      return unflattenNestedFields(flatResult);
    } catch {
      return {};
    }
  },

  serialize: (filters: RawFilters) => {
    const flattenedFilters = flattenNestedFields(filters);
    const parts: string[] = [];

    Object.entries(flattenedFilters).forEach(([field, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        value !== "" &&
        (Array.isArray(value) ? value.length > 0 : true)
      ) {
        if (
          typeof value === "object" &&
          !Array.isArray(value) &&
          ("from" in value || "to" in value)
        ) {
          const dateRange = value as { from?: Date; to?: Date };
          const dates: string[] = [];

          if (dateRange.from) {
            const fromDate = new Date(dateRange.from);
            if (!Number.isNaN(fromDate.getTime())) {
              dates.push(fromDate.toISOString());
            }
          }
          if (dateRange.to) {
            const toDate = new Date(dateRange.to);
            if (!Number.isNaN(toDate.getTime())) {
              dates.push(toDate.toISOString());
            }
          }

          if (dates.length > 0) {
            const op =
              dates.length === 1 ? (dateRange.from ? "gte" : "lte") : "between";
            parts.push(`${field}_${op}=${dates.join(",")}`);
          }
        } else if (
          typeof value === "object" &&
          !Array.isArray(value) &&
          ("min" in value || "max" in value)
        ) {
          const range = value as { min?: number; max?: number };
          const values: string[] = [];

          if (range.min !== undefined) values.push(String(range.min));
          if (range.max !== undefined) values.push(String(range.max));

          if (values.length > 0) {
            const op =
              values.length === 1
                ? range.min !== undefined
                  ? "min"
                  : "max"
                : "between";
            parts.push(`${field}_${op}=${values.join(",")}`);
          }
        } else if (isLikeFilter(value)) {
          parts.push(`${field}_${value.op}=${encodeURIComponent(value.value)}`);
        } else {
          if (Array.isArray(value)) {
            const filteredArray = value.filter(
              (item) =>
                item !== undefined &&
                item !== null &&
                item !== "" &&
                String(item).trim() !== "",
            );

            if (filteredArray.length > 0) {
              const queryValue = filteredArray.join(",");
              parts.push(`${field}_in=${queryValue}`);
            }
          } else {
            const queryValue = String(value);
            parts.push(`${field}_eq=${queryValue}`);
          }
        }
      }
    });

    return parts.join("&");
  },

  eq: (a: RawFilters, b: RawFilters) => {
    const flattenedA = flattenNestedFields(a);
    const flattenedB = flattenNestedFields(b);

    const aKeys = Object.keys(flattenedA || {}).filter(
      (key) =>
        flattenedA[key] !== null &&
        flattenedA[key] !== undefined &&
        !(Array.isArray(flattenedA[key]) && flattenedA[key].length === 0),
    );
    const bKeys = Object.keys(flattenedB || {}).filter(
      (key) =>
        flattenedB[key] !== null &&
        flattenedB[key] !== undefined &&
        !(Array.isArray(flattenedB[key]) && flattenedB[key].length === 0),
    );

    if (aKeys.length === 0 && bKeys.length === 0) return true;

    if (aKeys.length !== bKeys.length) return false;

    return aKeys.every((key) => a[key] === b[key]);
  },
});

export const useQueryFilters = <T extends string = string>({
  defaultValue,
  storageKey: _storageKey,
  queryKeyPrefix = "",
}: {
  defaultValue?: RawFilters<T>;
  storageKey?: string;
  queryKeyPrefix?: string;
}) => {
  const { tenantId } = useTenant();

  const storageKey = _storageKey
    ? `${_storageKey}__filters_${tenantId}`
    : undefined;

  const [filters, setFilters] = useQueryState(
    getQueryKey(queryKeyPrefix, "q"),
    {
      ...filtersParser,
      defaultValue: {} as RawFilters<T>,
      history: "replace",
      shallow: false,
    },
  );

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized) return;

    if (typeof window !== "undefined") {
      let mergedFilters: RawFilters<T> = {
        ...(defaultValue || {}),
        ...(filters || {}),
      };

      if (storageKey) {
        const sessionFiltersRaw = window.sessionStorage.getItem(storageKey);
        const sessionFilters: RawFilters<T> = sessionFiltersRaw
          ? JSON.parse(sessionFiltersRaw)
          : {};

        mergedFilters = {
          ...(defaultValue || {}),
          ...sessionFilters,
          ...(filters || {}),
        };
      }

      setFilters(mergedFilters);

      if (storageKey) {
        window.sessionStorage.setItem(
          storageKey,
          JSON.stringify(mergedFilters),
        );
      }

      setInitialized(true);
    }
  }, [initialized, filters, setFilters, storageKey, defaultValue]);

  const serverSideFilters = useMemo(
    () => convertToServerFilters(filters || {}),
    [filters],
  );

  const handleSetFilters = useCallback(
    (newFilters: RawFilters<T>) => {
      const mergedFilters = { ...filters, ...newFilters };

      Object.keys(newFilters).forEach((key) => {
        const k = key as keyof RawFilters<T>;
        if (newFilters[k] === undefined || newFilters[k] === null) {
          delete mergedFilters[k];
        }
      });

      setFilters(mergedFilters);
      if (typeof window !== "undefined" && storageKey) {
        window.sessionStorage.setItem(
          storageKey,
          JSON.stringify(mergedFilters),
        );
      }
    },
    [setFilters, filters, storageKey],
  );

  return {
    filters,
    setFilters: handleSetFilters,
    serverSideFilters,
    initialized,
  };
};

export type { RawFilters, ServerFilters, FilterValue, LikeFilter };

export const useQuerySort = ({
  defaultSort = { sort: "createdAt", order: "desc" },
  storageKey: _storageKey,
  queryKeyPrefix = "",
}: {
  defaultSort?: { sort: string; order: "asc" | "desc" };
  storageKey?: string;
  queryKeyPrefix?: string;
}) => {
  const { tenantId } = useTenant();

  const storageKey = _storageKey
    ? `${_storageKey}__sort_${tenantId}`
    : undefined;

  const [sortRaw, setSort] = useQueryState(
    getQueryKey(queryKeyPrefix, "sort"),
    {
      history: "replace",
      shallow: false,
    },
  );
  const [orderRaw, setOrder] = useQueryState(
    getQueryKey(queryKeyPrefix, "order"),
    {
      history: "replace",
      shallow: false,
    },
  );

  const [initialized, setInitialized] = useState(false);
  const [sort, setSortState] = useState(defaultSort.sort);
  const [order, setOrderState] = useState<"asc" | "desc">(defaultSort.order);

  useEffect(() => {
    if (initialized) return;

    if (typeof window !== "undefined") {
      let mergedSort = sortRaw ?? defaultSort.sort;
      let mergedOrder = (orderRaw ?? defaultSort.order) as "asc" | "desc";

      if (storageKey) {
        const sessionSortRaw = window.sessionStorage.getItem(storageKey);
        let sessionSort: { sort: string; order: string } = {
          sort: defaultSort.sort,
          order: defaultSort.order,
        };

        try {
          sessionSort = sessionSortRaw
            ? JSON.parse(sessionSortRaw)
            : sessionSort;
        } catch (e) {
          console.error("Invalid session storage sort data", e);
        }

        mergedSort = sortRaw ?? sessionSort.sort ?? defaultSort.sort;
        mergedOrder = (orderRaw ?? sessionSort.order ?? defaultSort.order) as
          | "asc"
          | "desc";
      }

      setSortState(mergedSort);
      setOrderState(mergedOrder);

      setSort(mergedSort);
      setOrder(mergedOrder);

      if (storageKey) {
        window.sessionStorage.setItem(
          storageKey,
          JSON.stringify({ sort: mergedSort, order: mergedOrder }),
        );
      }

      setInitialized(true);
    }
  }, [
    initialized,
    sortRaw,
    orderRaw,
    setSort,
    setOrder,
    defaultSort,
    storageKey,
  ]);

  useEffect(() => {
    if (typeof window !== "undefined" && initialized && storageKey) {
      window.sessionStorage.setItem(
        storageKey,
        JSON.stringify({ sort, order }),
      );
    }
  }, [sort, order, initialized, storageKey]);

  const sorting = useMemo(() => {
    return [{ id: sort, desc: order === "desc" }];
  }, [sort, order]);

  const onSortingChange: OnChangeFn<SortingState> = useCallback(
    (sortingState) => {
      const newState =
        typeof sortingState === "function"
          ? sortingState(sorting)
          : sortingState;

      const newSort = newState[0].id;
      const newOrder = newState[0].desc ? "desc" : "asc";

      setSort(newSort);
      setOrder(newOrder);

      setSortState(newSort);
      setOrderState(newOrder);
    },
    [setSort, setOrder, sorting],
  );

  return { sorting, onSortingChange, sort, order, initialized };
};

export const useQueryGlobalSearch = ({
  defaultValue = "",
  storageKey: _storageKey,
  queryKeyPrefix = "",
}: {
  defaultValue?: string;
  storageKey?: string;
  queryKeyPrefix?: string;
}) => {
  const { tenantId } = useTenant();

  const storageKey = _storageKey
    ? `${_storageKey}__global_search_${tenantId}`
    : undefined;

  const [globalSearch, setGlobalSearch] = useQueryState(
    getQueryKey(queryKeyPrefix, "global_search"),
    {
      defaultValue,
    },
  );

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized) return;

    if (typeof window !== "undefined") {
      const sessionValue = storageKey
        ? window.sessionStorage.getItem(storageKey) || ""
        : "";

      const mergedValue = globalSearch || sessionValue || "";

      setGlobalSearch(mergedValue);

      if (storageKey) {
        window.sessionStorage.setItem(storageKey, mergedValue);
      }

      setInitialized(true);
    }
  }, [initialized, globalSearch, setGlobalSearch, storageKey]);

  useEffect(() => {
    if (typeof window !== "undefined" && initialized && storageKey) {
      window.sessionStorage.setItem(storageKey, globalSearch || "");
    }
  }, [globalSearch, initialized, storageKey]);

  return { globalSearch, setGlobalSearch, initialized };
};

export const useServerSideQuery = <T extends string = string>({
  storageKey,
  defaultGlobalSearch,
  defaultFilters = {},
  defaultSorting = { sort: "createdAt", order: "desc" },
  queryKeyPrefix = "",
}: {
  storageKey?: string;
  defaultGlobalSearch?: string;
  defaultFilters?: RawFilters<T>;
  defaultSorting?: { sort: string; order: "asc" | "desc" };
  queryKeyPrefix?: string;
}) => {
  const { tenantId } = useTenant();

  const {
    globalSearch,
    setGlobalSearch,
    initialized: initializedGlobalSearch,
  } = useQueryGlobalSearch({
    storageKey,
    defaultValue: defaultGlobalSearch,
    queryKeyPrefix,
  });
  const {
    filters,
    serverSideFilters,
    setFilters,
    initialized: initializedFilters,
  } = useQueryFilters<T>({
    storageKey,
    defaultValue: defaultFilters,
    queryKeyPrefix,
  });
  const {
    sorting,
    onSortingChange,
    sort,
    order,
    initialized: initializedSort,
  } = useQuerySort({
    storageKey,
    defaultSort: defaultSorting,
    queryKeyPrefix,
  });

  const initialized =
    initializedGlobalSearch && initializedFilters && initializedSort;

  const hasActiveFilters = useMemo(() => {
    return JSON.stringify(filters) !== JSON.stringify(defaultFilters);
  }, [filters, defaultFilters]);

  const resetToDefaultFilters = useCallback(() => {
    if (typeof window !== "undefined" && storageKey) {
      window.sessionStorage.removeItem(`${storageKey}__filters_${tenantId}`);
      window.sessionStorage.removeItem(`${storageKey}__sort_${tenantId}`);
      window.sessionStorage.removeItem(
        `${storageKey}__global_search_${tenantId}`,
      );
    }

    setFilters(defaultFilters);
    setGlobalSearch(defaultGlobalSearch ?? "");

    setTimeout(() => {
      window.location.reload();
    }, 100);
  }, [
    tenantId,
    storageKey,
    setFilters,
    defaultFilters,
    setGlobalSearch,
    defaultGlobalSearch,
  ]);

  const initialServerSideFilters = useMemo(() => {
    return convertToServerFilters(defaultFilters);
  }, [defaultFilters]);

  return {
    sort,
    order,
    filters,
    sorting,
    setFilters,
    initialized,
    globalSearch,
    setGlobalSearch,
    onSortingChange,
    hasActiveFilters,
    serverSideFilters,
    initialServerSideFilters,
    resetFilters: resetToDefaultFilters,
  };
};

export const useServerSideFilters = <T extends string = string>({
  query,
}: {
  query: RawFilters<T>;
}) => {
  const serverSideFilters = useMemo(
    () => convertToServerFilters(query || {}),
    [query],
  );

  return serverSideFilters;
};

export function convertRawToColumnFilters(rawFilters: RawFilters) {
  return Object.keys(rawFilters).map((rawFilter) => ({
    id: rawFilter,
    value: rawFilters[rawFilter as keyof typeof rawFilters],
  }));
}

export function filterItemWithRawFilters<T, K extends keyof T & string>(
  item: T,
  rawFilters: RawFilters<K>,
): boolean {
  return Object.entries(rawFilters).every(([key, filterValue]) => {
    if (filterValue === undefined || filterValue === null) return true;
    if (!Object.prototype.hasOwnProperty.call(item, key)) return true; // If field doesn't exist, don't filter out
    const itemValue = (item as any)[key];

    if (
      typeof filterValue === "object" &&
      filterValue !== null &&
      ("from" in filterValue || "to" in filterValue)
    ) {
      const { from, to } = filterValue as { from?: Date; to?: Date };
      if (from && itemValue < from) return false;
      if (to && itemValue > to) return false;
      return true;
    }

    if (
      typeof filterValue === "object" &&
      filterValue !== null &&
      ("min" in filterValue || "max" in filterValue)
    ) {
      const { min, max } = filterValue as { min?: number; max?: number };
      if (typeof itemValue !== "number") return false;
      if (min !== undefined && itemValue < min) return false;
      if (max !== undefined && itemValue > max) return false;
      return true;
    }

    if (Array.isArray(filterValue)) {
      return filterValue.some((val) => itemValue === val);
    }

    if (isLikeFilter(filterValue)) {
      if (typeof itemValue !== "string") return false;
      const pattern = filterValue.value.replace(/%/g, ".*").replace(/_/g, ".");
      const regex = new RegExp(`^${pattern}$`, "i");
      const matches = regex.test(itemValue);
      return filterValue.op === "iLike" ? matches : !matches;
    }

    return itemValue === filterValue;
  });
}
